import { AshbyConfig } from '@dadohr/shared/domainTypes/integrations/ashby';
import { CF } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  MASK,
  masked
} from '../common';
import { removeAshbyConfig, useAshbyConfig } from './service';

const NAME = 'Ashby';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const SHOW_DOC_LINK = false;

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<AshbyConfig>) => {
  const { t } = useUnsafeTranslation();

  const { apiKey } = config?.data || {
    apiKey: ''
  };

  const [state, setState] = useState<{
    apiKey: string;
  }>({
    apiKey: apiKey ? MASK : ''
  });
  useEffect(() => {
    setState({ apiKey: masked(apiKey) });
  }, [apiKey]);

  if (!config) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Connect Ashby and Dado to import your employee data')}
        </Alert>
        {SHOW_DOC_LINK && (
          <Typography component="p" variant="body1">
            <LinkExternal
              color="primary"
              href={
                'https://ashbyhq.notion.site/How-do-I-generate-an-API-key-58d3409382014a3d80c3b08e982644d7'
              }
            >
              {t('Create an API Key in Ashby')}
            </LinkExternal>{' '}
            {t('and provide it below.')}
          </Typography>
        )}

        <FormRow>
          <CustomInputLabel
            label={t('org.integrations.common.apiKey')}
            fullWidth
          >
            <TextField
              variant="outlined"
              value={state.apiKey}
              onChange={(ev) =>
                setState((s) => ({ ...s, apiKey: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeAshbyConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
            disabled={!apiKey}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={() =>
                callFirebaseFunction(CF.ashby.verifyCredentialsAndStore, {
                  orgId: config.data.orgId,
                  apiKey: state.apiKey
                }).then(onSuccessfulIntegration)
              }
              disabled={!state.apiKey || state.apiKey === config.data.apiKey}
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

export const AshbySettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useAshbyConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      name={NAME}
      functionalities={functionalities}
      logoUrl="/images/integrations-logos/ashby.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
